<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { format, isValid } from "date-fns";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CheckIcon, ReceiptRefundIcon } from '@heroicons/vue/24/outline'
import adminAPI from '@/services/api';
import RadioList from "../ui/radio/RadioList.vue";
import Alert from '../ui/alerts/Alert.vue';
import { getUserRoles } from '@/utils/current-user';
import { useClaimStore, useNotificationStore } from '@/store';
import {
  form,
  causesOptions
} from './DefaultAdditionalLossForm';
import Expenses from '../expenses/Expenses.vue';

import type { ClaimOverview } from '@/pages/plugin/AgentFeedback.vue';
import BaseButton from '../ui/buttons/BaseButton.vue';
import MountedTeleport from '../MountedTeleport.vue';
import { TrashIcon } from 'lucide-vue-next';
import { useRoute, useRouter } from 'vue-router';
import { DocumentCheckIcon, ForwardIcon } from '@heroicons/vue/24/solid';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/vue/24/outline';

const props = defineProps<{ claimOverview: ClaimOverview }>();
const emits = defineEmits(["nextStep"]);

const isLoading = ref(false);
async function submitForm() {
  try {
    isLoading.value = true;
    const res = await adminAPI.saveAgentFeedbackAdditionalLoss(props.claimOverview.claimId, form.value);
    if (res?.success) {
      useClaimStore().retrieveClaim(useClaimStore().claim.id);
      emits("nextStep");
    }
  } finally {
    isLoading.value = false;
  }
}

const closeClaimLoader = ref(false);
async function closeClaim() {
  try {
    closeClaimLoader.value = true;
    const res = await adminAPI.addEvent(props.claimOverview.claimId, { machineEvent: "Abandon" });
    useNotificationStore().emitNotification({ title: "Dossier abandonné avec succès", message: "Vous pouvez fermer cette page ou naviguer sur un autre dossier" });
    useClaimStore().retrieveClaim(useClaimStore().claim.id);
    useRouter().push({ name: "ClaimOverview", params: { id: props.claimOverview.claimId } });

    await adminAPI.saveAgentFeedbackAdditionalLoss(props.claimOverview.claimId, 
      {...form.value, note: form.value.note || "Abandon"}
    ); // So that we know this form step is completed
  } finally {
    closeClaimLoader.value = false;
  }
}

async function setRequestDate() {
  try {
    await adminAPI.setAdditionalLossRequestDate(props.claimOverview.claimId);
    await useClaimStore().refreshClaim();
    useNotificationStore().emitNotification({ title: "Information enregistrée" });
  } catch (error) {
    console.error(error);
    useNotificationStore().emitNotification({
      type: "error",
      title: "Une erreur est survenue, merci de nous contacter"
    });
  }
}

// Watchers
watch(form.value, (newValue) => {
  // There is only one field for 2 properties, so we manually update the second one
  form.value.totalOtherLoss.paidBy = newValue.lostAccommodation.paidBy
});

const disabledForAgent = ref(!Boolean(props.claimOverview.form?.b2c)
  && getUserRoles().includes('CUSTOMER_AGENT')
);

const open = ref(false); // Modal
const customerNoteData = computed(() => useClaimStore().claim.customerNotes);
</script>
<template>
  <div>

    <MountedTeleport to="#teleport-agent-feedback-alert">
      <div v-if="claimOverview.travelAgencyCompanyName || claimOverview.isOfficeB2b">
        <Alert v-if="!claimOverview.form?.additionalLossRequestSentAt"
          type="warning"
          title="Attente des préjudices supplémentaires"
          :title-note="`Le ${format(new Date(claimOverview.form?.lastModified || claimOverview.form?.createdAt || 0), 'dd/MM/yyyy')}`"
          message="Nous avons besoin de votre retour afin de constituer d'éventuels dommages supplémentaires"
        >
          <div class="py-4">
            <BaseButton text="J'ai envoyé la demande de préjudices" @click="setRequestDate" />
          </div>
        </Alert>
        <Alert v-else
          type="info"
          title="Vous avez indiqué avoir pris contact avec l'entreprise"
          :title-note="`Le ${format(new Date(claimOverview.form.additionalLossRequestSentAt || 0), 'dd/MM/yyyy')}`"
        >
          <!-- <div class="py-4">
            <BaseButton text="J'ai envoyé une relance" />
          </div> -->
        </Alert>
      </div>
    </MountedTeleport>

    <!-- After submit modal -->
    <TransitionRoot as="template" :show="open">
      <Dialog as="div" class="relative z-10" @close="open = false">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
                    <CheckIcon class="w-6 h-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Informations enregistrées</DialogTitle>
                    <div class="mt-2">
                      <!-- <p class="text-sm text-slate-500">Nous avons bien enregistré votre retour sur ce dossier d'indemnisation.</p> -->
                      <p class="mt-4 text-sm text-slate-500">Un email contenant les documents à faire signer aux passagers va vous être envoyé.</p>
                    </div>
                  </div>
                </div>
                <div class="mt-5 sm:mt-6">
                  <router-link :to="{ name: 'ClaimOverview', params: { id: claimOverview.claimId } }" class="inline-flex justify-center w-full px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    @click="open = true">
                    Retourner au dossier
                  </router-link>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <div v-if="disabledForAgent">
      <Alert
        title="L’équipe chargée de comptes s’occupe de cette partie et d’obtenir les signatures"
        type="success"
      />
    </div>
    <div v-else class="px-2">

      <div class="py-4 cursor-help" v-if="false && claimOverview.travelAgencyCompany">
        <Alert
          v-if="!claimOverview.travelAgencyCompany?.isDirectPassengerContactAllowed"
          title="Contact en direct des voyageurs non autorisé"
          :message="`${claimOverview.travelAgencyCompanyName} ne nous autorise pas à contacter les employés directement`"
          type="warning"
          :with-border="false"
        />
        <div v-else class="cursor-help"  v-tooltip.bottom="'Upclaim s\'occupe de faire signer les passagers'">
          <Alert
            title="Signature voyageur en direct"
            :message="`${claimOverview.travelAgencyCompanyName} nous autorise à faire signer les employés.`"
            type="success"
            :with-border="true"
          />
        </div>
      </div>

      <div v-permission="['CUSTOMER_ACCOUNT_MANAGER', 'UPCLAIM_ADMIN']" v-if="Boolean(customerNoteData)" class="pt-4 pb-6 border-b">
        <h2 class="inline-flex mb-5 text-base font-semibold leading-7 text-gray-900 align-center">
          <ChatBubbleBottomCenterTextIcon class="w-5 h-5 mt-1 mr-2 text-gray-700" />
          Notes Upclaim à destination de l'agence :
        </h2>
        <div class="mt-2">
          <blockquote class="px-6 mt-2 mb-3 text-base font-medium prose whitespace-pre-line gray-700">
            « <span class="italic">{{ customerNoteData }}</span> »
          </blockquote>
        </div>
      </div>

      <div class="mt-2">
        <div class="col-span-full gap-x-6 gap-y-8 sm:grid-cols-6">
          <form class="-mx-8">
            <Expenses :claim-expenses="useClaimStore().claim.claimExpenses" @update="useClaimStore().refreshClaim()">
              <template v-slot:header>
                <div class="px-3 text-left">
                  <dt class="text-base font-medium leading-6 text-left text-gray-900">
                    <span class="inline-flex items-center">
                      <ReceiptRefundIcon class="w-5 h-5 mr-2 text-gray-700" />
                      Nous communiquer des dépenses
                    </span>
                  </dt>
                </div>
                <div class="px-6 my-6 text-sm leading-7 text-gray-700">
                  <p class="">
                    En plus d'une potentielle indemnisation forfaitaire de <span class="font-semibold text-gray-700">{{ claimOverview.ec261CompensationAmount }}€</span>
                    par voyageur (règlement européen 261/2004), des <span class="font-semibold text-gray-700">frais supplémentaires pourraient être remboursés</span>.
                  </p>
                  <div class="mt-6" v-if="claimOverview.travelAgencyCompanyName">
                    <p>Entreprise impactée :</p>
                    <ul class="px-4 font-medium text-gray-800 list-disc">
                      <li class="mt-2 list-inside">
                        {{ claimOverview.travelAgencyCompanyName }}
                      </li>
                    </ul>
                  </div>
                </div>
              </template>
            </Expenses>
          </form>

          <div class="px-2 mt-3">
            <Alert
              type="info" :with-border="false"
              title="Si les passagers ont subis un préjudice particulier, merci de nous l'indiquer dans la note ci-dessous"
            />
          </div>

          <div class="my-12 border-t border-gray-900/10">
            <!-- <div class="py-6 ">
              <h2 class="text-base font-semibold leading-7 text-gray-900">
                Cause de l'incident
                <span class="ml-1 text-xs font-normal text-gray-700">(optionnel)</span>
              </h2>
            </div>

            <form class="space-y-8">
              <div class="px-5">
                <RadioList label="Cause de l'annulation" :options="causesOptions" :selected-option="4"
                  v-model="form.cause"
                />
              </div>
            </form> -->
          </div>
        </div>
      </div>

      <div class="mt-10">
        <label for="comment" class="block text-base font-medium leading-6 text-gray-900">Nous joindre une note</label>
        <div class="mt-2">
          <textarea :rows="4" name="comment" id="comment" v-model="form.note"
            class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
        </div>
      </div>

      <div class="flex items-center justify-end mt-6 gap-x-6">
        <!-- <button type="button" class="text-sm font-semibold leading-6 text-gray-900">Cancel</button> -->
        <BaseButton text="Clôturer" type="primary" :icon="(TrashIcon as any)" :loading="closeClaimLoader" @click="closeClaim" />
        <BaseButton text="Valider les dépenses" type="submit" :icon="DocumentCheckIcon" :loading="isLoading" @click="submitForm" />
      </div>
    </div>
  </div>
</template>